<template>
    <div class="col-lg-8 mx-auto">

        <SubSection v-if="viewModel.canShowDashboardOption" :title="viewModel.dashboardTitle" :show-help="false">
            <p class="mt-2">
                {{ viewModel.dashboardText }}
            </p>

            <button class="btn btn-primary" @click="onDashboardClicked">Dashboard</button>
        </SubSection>

        <SubSection :title="viewModel.mainJourneyReferenceText" :show-help="false">
            <p v-for="(introText, i) in viewModel.mainJourneyText" :key="i" :class="{ 'mt-3': i === 0 }">{{ introText }}</p>

            <p v-if="viewModel.showAdviceLink">
                <a href="/howtocompletedesign" target="_blank">Find out about building a model of your retirement.</a>
            </p>

            <p class="steps-complete">Steps complete: {{ viewModel.completedSteps }}/{{ viewModel.totalSteps }}</p>

            <button class="btn btn-primary mb-3" @click="onMainJourneyClicked">Continue</button>
        </SubSection>

        <p v-if="viewModel.showTermsOfUse"><small>By continuing, you are agreeing to our <a target="_blank" :href="viewModel.termsOfUseResource">terms of use</a>.</small></p>
    </div>

    <modal title="Data sharing consent" ref="consentModal" idKey="consentModalKey">
        <consent-capture :info="consentInfo" @finished="onMissingConsentsCaptured"></consent-capture>
    </modal>
</template>
  
<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { workflow } from "@/guiide/ts/workflow/workflow";
import { WorkflowStep } from "@/guiide/ts/workflow/workflowStep";
import ConsentInfo, { emptyConsentInfo } from "../ts/consent/consentInfo";
import PermittedEntityType from "../ts/consent/permittedEntityType";
import { Consent } from "../ts/consent";
import SubSection from "@/guiide/components/common/SubSection.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import ConsentCapture from "./consent/ConsentCapture.vue";

type ViewModel = {
    canShowDashboardOption: boolean,
    dashboardTitle: string,
    dashboardText: string,
    mainJourneyReferenceText: string,
    mainJourneyText: string[],
    showAdviceLink: boolean,
    completedSteps: string,
    totalSteps: string,
    showTermsOfUse: boolean,
    termsOfUseResource: string
}

const store = useStore();
const workflowSteps = ref<Array<WorkflowStep>>(null);
const consentModal = ref(null);
const modalIsOpen = ref(false);
const consentInfo = ref<ConsentInfo>(emptyConsentInfo);

onMounted(async () => {
  workflowSteps.value = await workflow.getSteps();
});

watch(
    () => [ store.state.configuration, store.state.retiree ],
    ([config, retiree]) => {
        if (config == null || retiree == null || modalIsOpen.value) {
            return;
        }

        if (retiree.missingConsents.length > 0) {
            const missingConsent: Consent = retiree.missingConsents[0];
            modalIsOpen.value = true;
            consentInfo.value = new ConsentInfo(
                config.companyFullName, 
                config.companyAbbrevName, 
                PermittedEntityType.ExternalAgency,
                missingConsent.permittedEntityId,
                missingConsent.type);
            consentModal.value.showModal();
        }
    }
)

const viewModel = computed<ViewModel>(() => {
    const config = store.state.configuration;
    const retiree = store.state.retiree;

    if (config == null || retiree == null) {
        return getEmptyViewModel();
    }

    const canShowDashboardOption = retiree.isRegistered && !retiree.mustConfirmRetired;

    let showAdviceLink = false;
    let mainJourneyText: string[];
    
    if (!retiree.hasFinishedDesign) {
        mainJourneyText = config.userHomeMainJourneyIntroText;
        showAdviceLink = true;
    } else if (!retiree.isRegistered) {
        mainJourneyText = [ "Please enter the main plan journey if you would like to make some changes to what you want, alter your design in other ways, or examine your plan."] ;
    } else {
        mainJourneyText = [ "You only need to enter the main plan journey again if you want to make some fundamental changes to what you want, or your health has changed. If not, just go straight to the Dashboard." ];
    }

    return {
        canShowDashboardOption,
        dashboardTitle: config.userHomeTrackIntroTitle,
        dashboardText: config.userHomeTrackIntroText,
        mainJourneyReferenceText: config.mainJourneyReferenceText,
        mainJourneyText,
        showAdviceLink,
        completedSteps: config.workflow.stepsCompleted.toString(),
        totalSteps: workflowSteps.value?.length.toString() ?? "",
        showTermsOfUse: config.showTermsOfUse,
        termsOfUseResource: config.termsOfUseResource
    }
});

function onDashboardClicked() {
    const config = store.state.configuration;
    window.location.href = `/${config.nav.track.resource}`;
}

function onMainJourneyClicked() {
    window.location.href = `/home/startdesign`;
}

function onMissingConsentsCaptured() {
    consentModal.value.closeModal();
    modalIsOpen.value = false;
}

function getEmptyViewModel() {
    return {
        canShowDashboardOption: false,
        dashboardTitle: "",
        dashboardText: "",
        mainJourneyReferenceText: "",
        mainJourneyText: [],
        showAdviceLink: false,
        completedSteps: "",
        totalSteps: "",
        showTermsOfUse: false,
        termsOfUseResource: ""
    };
}

</script>
  